import React, { useEffect, useCallback, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import Container from "@mui/material/Container";
import Box from "components/common/Box";
import Stack from "components/common/Stack";
import Text from "components/common/Text";
import SectionHeading from "components/allInOneDashboard/SectionHeading";
import LockedProductCard from "components/allInOneDashboard/LockedProductCard";
import UnlockedProductCard from "components/allInOneDashboard/UnlockedProductCard";
import MobileEssentialCard from "components/allInOneDashboard/MobileEssentialCard";
import PortfolioHealthCheckBanner from "components/allInOneDashboard/PortfolioHealthCheckBanner";
import DashboardAdBanner from "components/allInOneDashboard/DashboardAdBanner";
import PerformanceWidget from "components/allInOneDashboard/PerformanceWidget";
import PrivateWealthSection from "components/allInOneDashboard/PrivateWealthSection";
import FeaturedBlogCard from "components/allInOneDashboard/FeaturedBlogCard";
import AllInOneFooter from "components/allInOneDashboard/AllInOneFooter";
import MarketPulseCard from "components/allInOneDashboard/MarketPulse";
import EssentialCardSkeleton from "components/allInOneDashboard/EssentialCardSkeleton";
import {
  hasProductPendingCall,
  getProductPendingCount,
} from "components/allInOneDashboard/utils/pendingCall";
import { usePromoAd } from "hooks/usePromoAd";
import { useDashboardOffer } from "hooks/useDashboardOffer";
import { useDashboardIconImages } from "hooks/useDashboardIconImages";
import { useFetch } from "hooks/useFetch";
import { localStore } from "stores/localStorage";
import { axios } from "helper/axios";
import { thirdpartyConstants } from "stores/thirdparty/thirdpartyConstants";
import { config } from "config";
import { jarvisOnestockOnboardingApis } from "stores/jarvisOnestock/jarvisOnestockOnboarding/jarvisOnestockOnboardingApis";
import { jarvisOnestockOnboardingParser } from "stores/jarvisOnestock/jarvisOnestockOnboarding/jarvisOnestockOnboardingParser";
import { onboardingApis } from "stores/onboarding/onboardingApis";
import { onboardingParsers } from "stores/onboarding/onboardingParsers";
import { jarvisRMSOnboardingApis } from "stores/jarvisRMS/jarvisRMSOnboarding/jarvisRMSOnboardingApis";
import { jarvisRMSOnboardingParser } from "stores/jarvisRMS/jarvisRMSOnboarding/jarvisRMSOnboardingParser";
import { useLocation } from "react-router-dom";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  BRAND_BLUE,
  MOBILE_GUTTER,
  MOBILE_SECTION_GAP,
} from "components/allInOneDashboard/constants/mobileTheme";

const isPhcProduct = (product) =>
  product?.code === config.productCodes.PHC ||
  product?.id === "phc" ||
  product?.code === "PHC";

const ESSENTIALS_BG_BANNER =
  "/images/dashboard/esissintial-background-ganpati.png";
const ESSENTIALS_BG_BANNER_MOBILE =
  "/images/dashboard/esissintial-background-ganpati-mobile.png";
const ESSENTIALS_OFFER_ICON = "/images/dashboard/essentials-ganpati-icon.png";
const ESSENTIALS_DEFAULT_ICON =
  "/images/product-info/investing-essentials-icon.png";

const essentialsCarouselSx = {
  display: { xs: "flex", md: "grid" },
  gridTemplateColumns: {
    md: "repeat(2, minmax(0, 1fr))",
    lg: "repeat(4, minmax(0, 1fr))",
  },
  gap: { xs: 1.5, md: 2.5 },
  overflowX: { xs: "auto", md: "visible" },
  scrollSnapType: { xs: "x mandatory", md: "none" },
  WebkitOverflowScrolling: "touch",
  alignItems: "stretch",
  pb: 1,
  mx: { xs: -MOBILE_GUTTER, md: -3 },
  px: { xs: MOBILE_GUTTER, md: 3 },
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": { display: "none" },
};

const UnifiedDashboardContainer = ({
  blogs,
  investingEssentials,
  privateWealth,
  disabledProductCodes,
  productsLoading,
  pendingCallData,
  getPortfolioOnboardingDataAction,
  updatePortfolioOnboardingDataAction,
  updateOnestockOnboardingDataAction,
  updateRMSOnboardingDataAction,
  getRiskDetails,
  getFirstRender,
  getBlogsAction,
  getProductDetailsAction,
  getPendingCallAction,
}) => {
  const history = useHistory();
  const { ads: promoAds } = usePromoAd();
  const { isActive: isOfferActive } = useDashboardOffer();
  const { iconByProductCode } = useDashboardIconImages();
  const {
    data: marketPulseResponse,
    loading: marketPulseLoading,
    error: marketPulseError,
    refetch: refetchMarketPulse,
  } = useFetch({ type: "current" });
  const [isMarketPulseExpanded, setIsMarketPulseExpanded] = useState(false);

  const location = useLocation();

  // Single fetch on dashboard visit. Skip if product-details is already
  // in flight (guards against accidental remounts).
  useEffect(() => {
    if (location.pathname !== "/customer/dashboard") return;
    if (productsLoading) return;

    getProductDetailsAction?.();
    getPortfolioOnboardingDataAction?.();
    getPendingCallAction?.();
    if (!blogs?.length) {
      getBlogsAction?.();
    }
    // Action creators from connect/bindActionCreators are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const COLLAPSE_MS = 480;

  const getRiskDetailsForProduct = useCallback(
    async (productCode) => {
      try {
        const response = await axios.get(
          `${thirdpartyConstants.PORTFOLIO_SUBSCRIPTION_PLANS_API}?productCode=${productCode}`,
        );
        if (response?.[0]?.riskData) {
          getRiskDetails(response[0].riskData);
        }
        return response;
      } catch (error) {
        console.error("API Error:", error);
      }
    },
    [getRiskDetails],
  );

  // Refresh product onboarding flags before navigating so stale persisted
  // isOnboardingCompleted: 1 does not bounce locked users back to dashboard.
  const syncLockedProductOnboardingStatus = useCallback(
    async (product) => {
      const code = product.code;
      try {
        if (code === config.productCodes.ONESTOCK) {
          const res = await jarvisOnestockOnboardingApis.getOnboardingData();
          const parsed = jarvisOnestockOnboardingParser.onboardingData(res);
          updateOnestockOnboardingDataAction?.({
            currentStage: parsed.currentStage,
            isOnboardingCompleted: parsed.isOnboardingCompleted ?? 0,
            isPaymentCompleted: parsed.isPaymentCompleted ?? 0,
          });
          return;
        }
        if (code === config.productCodes.PORTFOLIO) {
          const res = await onboardingApis.getOnboardingData();
          const parsed = onboardingParsers.onboardingData(res);
          updatePortfolioOnboardingDataAction?.({
            currentStage: parsed.currentStage,
            isOnboardingCompleted: parsed.isOnboardingCompleted ?? 0,
            isPaymentCompleted: parsed.isPaymentCompleted ?? 0,
          });
          return;
        }
        if (code === config.productCodes.RMS) {
          const res = await jarvisRMSOnboardingApis.getOnboardingData();
          const parsed = jarvisRMSOnboardingParser.onboardingData(res);
          updateRMSOnboardingDataAction?.({
            currentStage: parsed.currentStage,
            isOnboardingCompleted: parsed.isOnboardingCompleted ?? 0,
            isPaymentCompleted: parsed.isPaymentCompleted,
          });
        }
      } catch (error) {
        console.error("Onboarding status sync failed:", error);
      }
    },
    [
      updateOnestockOnboardingDataAction,
      updatePortfolioOnboardingDataAction,
      updateRMSOnboardingDataAction,
    ],
  );

  const handleProductClick = useCallback(
    async (product) => {
      if (!product.onboardingPath) {
        if (product.productInfoPath) {
          if (product.code) {
            localStore.setActiveProduct(product.code);
          }
          history.push(product.productInfoPath);
        }
        return;
      }

      localStore.setActiveProduct(product.code);

      if (!product.isLocked) {
        history.push(product.dashboardPath);
        return;
      }

      // Locked product with a product-info page (e.g. Prime Know More)
      if (product.productInfoPath) {
        history.push(product.productInfoPath);
        return;
      }

      if (product.clearPlanIdOnStart) {
        updatePortfolioOnboardingDataAction({ planId: null });
      }

      await syncLockedProductOnboardingStatus(product);
      await getRiskDetailsForProduct(product.code);

      if (product.needsFirstRender) {
        getFirstRender(true);
      }

      history.push(product.onboardingPath);
    },
    [
      history,
      updatePortfolioOnboardingDataAction,
      syncLockedProductOnboardingStatus,
      getRiskDetailsForProduct,
      getFirstRender,
    ],
  );

  const handleAdClick = useCallback(
    (ad) => {
      if (!ad?.redirectUrl) {
        return;
      }

      if (ad.redirectType === "external") {
        window.open(
          ad.redirectUrl,
          ad.openInNewTab ? "_blank" : "_self",
          "noopener,noreferrer",
        );
        return;
      }

      history.push(ad.redirectUrl);
    },
    [history],
  );

  const handleBlogReadMore = useCallback(() => {
    const blog = blogs?.[0];
    if (blog?.readMoreLink) {
      window.open(blog.readMoreLink, "_blank", "noopener,noreferrer");
      return;
    }
    history.push("/knowledge-centre/blogs");
  }, [blogs, history]);

  const renderEssentialProduct = (product, { compact = false } = {}) => {
    if (compact) {
      return (
        <MobileEssentialCard
          key={product.code || product.id}
          product={product}
          onClick={() => handleProductClick(product)}
        />
      );
    }

    const card = product.isLocked ? (
      <LockedProductCard
        product={product}
        onClick={() => handleProductClick(product)}
      />
    ) : (
      <UnlockedProductCard
        product={product}
        onClick={() => handleProductClick(product)}
        hasPendingCall={hasProductPendingCall(pendingCallData, product)}
        pendingCount={getProductPendingCount(pendingCallData, product.code)}
      />
    );

    return (
      <Box
        key={product.code || product.id}
        sx={{
          flex: { xs: "0 0 auto", md: "unset" },
          width: {
            xs: "min(78vw, 280px)",
            sm: "clamp(240px, 44vw, 286px)",
            md: "auto",
          },
          minWidth: { md: 0 },
          scrollSnapAlign: { xs: "start", md: "unset" },
          minHeight: { xs: 280, md: 300 },
          height: "100%",
          display: "flex",
          "& > *": {
            flex: 1,
            width: "100%",
            minHeight: "100%",
          },
        }}
      >
        {card}
      </Box>
    );
  };

  const featuredBlog = blogs?.[0];
  // Only show skeletons on the true first load, never on background refetches
  // (e.g. after onboarding), so already-rendered cards don't flicker away.
  const showEssentialsSkeleton =
    productsLoading && investingEssentials.length === 0;

  const essentialsWithIcons = useMemo(
    () =>
      investingEssentials.map((product) => {
        if (isPhcProduct(product)) {
          return product;
        }
        const remoteUrl = iconByProductCode[product.code];
        if (!remoteUrl) {
          return product;
        }
        return { ...product, icon: remoteUrl, localIcon: product.icon };
      }),
    [investingEssentials, iconByProductCode],
  );

  const phcProduct = essentialsWithIcons.find(isPhcProduct);
  const mobileEssentials = essentialsWithIcons.filter((p) => !isPhcProduct(p));

  const handlePhcBannerClick = () => {
    if (phcProduct) {
      handleProductClick(phcProduct);
      return;
    }
    history.push("/customer/product-info/phc");
  };

  const essentialsSection = ({ compact = false, overlapMarketPulse = false } = {}) => {
    const showIllustratedBg = isOfferActive;

    return (
      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          overflow: "hidden",
          borderRadius: { xs: "0 0 24px 24px", md: "24px" },
          border: "1px solid rgba(0,0,0,0.05)",
          boxShadow: "0 0 9px rgba(0,0,0,0.11)",
          p: { xs: MOBILE_GUTTER, md: 3 },
          pt: compact && showIllustratedBg
            ? 10
            : overlapMarketPulse
            ? isMarketPulseExpanded
              ? { xs: 2, md: 3 }
              : { xs: 5.5, md: 6 }
            : { xs: 3, md: 3 },
          ...(overlapMarketPulse
            ? {
                "&&": {
                  mt: "calc(-10px - 30px) !important",
                },
                transition: `margin-top ${COLLAPSE_MS}ms cubic-bezier(0.4, 0, 0.2, 1), padding-top ${COLLAPSE_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`,
              }
            : { mt: { xs: 0, md: 0 } }),
          background: showIllustratedBg
            ? "#FDF9F2"
            : "radial-gradient(ellipse at top left, rgba(148,187,246,0.15), transparent 60%)",
        }}
      >
        {showIllustratedBg ? (
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 0,
              pointerEvents: "none",
              overflow: "hidden",
            }}
          >
            <Box
              component="img"
              src={compact ? ESSENTIALS_BG_BANNER_MOBILE : ESSENTIALS_BG_BANNER}
              alt=""
              sx={{
                position: "absolute",
                inset: 0,
                width: "102%",
                height: "108%",
                objectFit: "cover",
                objectPosition: compact ? "top center" : "right top",
              }}
            />
          </Box>
        ) : null}

        <Box sx={{ position: "relative", zIndex: 1 }}>
          <SectionHeading
            title="Investing Essentials"
            iconSrc={isOfferActive ? ESSENTIALS_OFFER_ICON : ESSENTIALS_DEFAULT_ICON}
            sx={showIllustratedBg ? { pr: { md: "240px" } } : undefined}
          />
          <Box sx={essentialsCarouselSx}>
            {showEssentialsSkeleton
              ? Array.from({ length: 4 }).map((_, index) => (
                  <EssentialCardSkeleton
                    key={`essential-skeleton-${index}`}
                    compact={compact}
                  />
                ))
              : (compact ? mobileEssentials : essentialsWithIcons).map(
                  (product) => renderEssentialProduct(product, { compact }),
                )}
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Container
      maxWidth="xl"
      sx={{ px: { xs: 0, md: 4, lg: 7.5 }, position: "relative" }}
    >
      {/* Mobile layout — Figma Main Dashboard */}
      <Stack
        spacing={MOBILE_SECTION_GAP}
        sx={{ display: { xs: "flex", md: "none" }, px: 0, pt: 1 }}
      >
        {essentialsSection({ compact: true, overlapMarketPulse: false })}

        <Box sx={{ px: MOBILE_GUTTER }}>
          <PortfolioHealthCheckBanner onClick={handlePhcBannerClick} />
        </Box>

        {/* <Box sx={{ px: MOBILE_GUTTER }}>
          <PerformanceWidget />
        </Box> */}

        <Box sx={{ px: MOBILE_GUTTER }}>
          <DashboardAdBanner ads={promoAds} onClick={handleAdClick} />
        </Box>

        <Box sx={{ px: MOBILE_GUTTER }}>
          <PrivateWealthSection
            products={privateWealth}
            disabledProductCodes={disabledProductCodes}
            onProductClick={handleProductClick}
            loading={productsLoading}
            pendingCallData={pendingCallData}
          />
        </Box>

        <Box sx={{ px: MOBILE_GUTTER }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Text
              sx={{
                fontSize: 18,
                fontWeight: 800,
                color: "#111827",
                letterSpacing: "-0.02em",
              }}
            >
              Blogs
            </Text>
            <Box
              component="button"
              type="button"
              onClick={() => history.push("/knowledge-centre/blogs")}
              sx={{
                border: 0,
                bgcolor: "transparent",
                cursor: "pointer",
                p: 0,
                font: "inherit",
                color: BRAND_BLUE,
                fontSize: 14,
                fontWeight: 500,
                display: "inline-flex",
                alignItems: "center",
                gap: 0.25,
              }}
            >
              See all
              <ChevronRightIcon sx={{ fontSize: 18 }} />
            </Box>
          </Stack>
          <FeaturedBlogCard
            blog={featuredBlog}
            onReadMore={handleBlogReadMore}
          />
        </Box>

        <AllInOneFooter />
      </Stack>

      {/* Desktop / tablet layout */}
      <Stack spacing={3} sx={{ display: { xs: "none", md: "flex" } }}>
        <Box
          sx={{
            position: "relative",
            zIndex: 5,
            mt: isMarketPulseExpanded ? -2.5 : -5,
            transition: `margin-top ${COLLAPSE_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`,
          }}
        >
          <MarketPulseCard
            response={marketPulseResponse}
            loading={marketPulseLoading}
            error={marketPulseError}
            onRetry={refetchMarketPulse}
            onExpandedChange={setIsMarketPulseExpanded}
          />
        </Box>

        {essentialsSection({ compact: false, overlapMarketPulse: true })}

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems="stretch"
        >
          <DashboardAdBanner ads={promoAds} onClick={handleAdClick} />
          {/* <PerformanceWidget /> */}
        </Stack>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2.5}
          alignItems="stretch"
          sx={{ width: "100%", minWidth: 0 }}
        >
          <Box
            sx={{
              flex: "1 1 0%",
              minWidth: 0,
              maxWidth: "100%",
              overflow: "hidden",
            }}
          >
            <PrivateWealthSection
              products={privateWealth}
              disabledProductCodes={disabledProductCodes}
              onProductClick={handleProductClick}
              loading={productsLoading}
              pendingCallData={pendingCallData}
            />
          </Box>
          <Box
            sx={{
              width: { xs: "100%", md: 319 },
              flex: { md: "0 0 319px" },
              flexShrink: 0,
              minWidth: 0,
            }}
          >
            <FeaturedBlogCard
              blog={featuredBlog}
              onReadMore={handleBlogReadMore}
            />
          </Box>
        </Stack>
      </Stack>
    </Container>
  );
};

export default UnifiedDashboardContainer;
